import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Clock, CheckCircle, XCircle, Users, Calendar, Search,
  Filter, Download, RefreshCw, MapPin, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { staffAttendanceAPI, lookupDataAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const attendanceSchema = z.object({
  branchId: z.string().min(1, 'Please select a branch'),
  date: z.string().min(1, 'Date is required'),
  staffId: z.string().min(1, 'Please select staff'),
  status: z.enum(['present', 'absent', 'late', 'half-day']),
  notes: z.string().optional(),
});

const StaffAttendance = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(attendanceSchema),
    defaultValues: {
      branchId: '',
      date: selectedDate,
      staffId: '',
      status: 'present',
      notes: '',
    },
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadStaffMembers(selectedBranch);
      loadAttendance(selectedBranch, selectedDate);
    }
  }, [selectedBranch, selectedDate]);

  const loadInitialData = async () => {
    try {
      const [branchesData] = await Promise.all([
        lookupDataAPI.getBranches()
      ]);
      
      setBranches((branchesData.branches || []).filter(branch => branch.is_active));
      
      // Set user's branch as default if available
      if (user?.branch_id) {
        setSelectedBranch(user.branch_id);
        setValue('branchId', user.branch_id);
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data', 'Please try again');
    }
  };

  const loadStaffMembers = async (branchId) => {
    try {
      const staffData = await lookupDataAPI.getUsers(branchId);
      setStaffMembers(staffData.users || []);
    } catch (error) {
      console.error('Failed to load staff members:', error);
    }
  };

  const loadAttendance = async (branchId, date) => {
    try {
      const attendanceData = await staffAttendanceAPI.getAttendance();
      // The API returns an array, not an object with attendances property
      setAttendanceData(Array.isArray(attendanceData) ? attendanceData : []);
    } catch (error) {
      console.error('Failed to load attendance:', error);
      setAttendanceData([]);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadStaffMembers(selectedBranch),
        loadAttendance(selectedBranch, selectedDate)
      ]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Use clock-in instead of mark attendance for manual marking
      const result = await staffAttendanceAPI.clockIn();
      
      toast.success('Attendance recorded successfully!', `Clocked in at ${result.clock_in_time}`);
      
      // Refresh attendance data
      await loadAttendance(data.branchId, data.date);
      reset();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to record attendance';
      toast.error('Failed to record attendance', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    try {
      const result = await staffAttendanceAPI.clockIn();
      toast.success('Clocked in successfully!', `Time: ${result.clock_in}`);
      refreshData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to clock in';
      toast.error('Failed to clock in', errorMessage);
    }
  };

  const handleClockOut = async () => {
    try {
      const result = await staffAttendanceAPI.clockOut();
      toast.success('Clocked out successfully!', `Time: ${result.clock_out}`);
      refreshData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to clock out';
      toast.error('Failed to clock out', errorMessage);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-700 border-green-200';
      case 'absent': return 'bg-red-100 text-red-700 border-red-200';
      case 'late': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'half-day': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return CheckCircle;
      case 'absent': return XCircle;
      case 'late': return Clock;
      case 'half-day': return AlertCircle;
      default: return Clock;
    }
  };

  const filteredAttendance = attendanceData.filter(record => {
    const staffName = record.staffName || record.staff_name || record.user_name || record.user_email || '';
    const roleName = record.role || record.role_name || '';
    const branchName = record.branch || record.branch_name || '';
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = staffName.toLowerCase().includes(searchLower) ||
                         roleName.toLowerCase().includes(searchLower) ||
                         branchName.toLowerCase().includes(searchLower);
    const matchesStatus = filterStatus === 'all' || record.status === filterStatus;
    const matchesDate = record.date === selectedDate;
    return matchesSearch && matchesStatus && matchesDate;
  });

  const attendanceStats = {
    total: filteredAttendance.length,
    present: filteredAttendance.filter(r => r.status === 'present').length,
    absent: filteredAttendance.filter(r => r.status === 'absent').length,
    late: filteredAttendance.filter(r => r.status === 'late').length,
    halfDay: filteredAttendance.filter(r => r.status === 'half-day').length,
  };

  const attendanceRate = attendanceStats.total > 0 
    ? ((attendanceStats.present / attendanceStats.total) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Staff Attendance</h1>
            <p className="text-gray-600 mt-2">Track and manage staff attendance records</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button variant="outline" size="sm">
              <Download size={16} className="mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Staff', value: attendanceStats.total, color: 'blue' },
          { label: 'Present', value: attendanceStats.present, color: 'green' },
          { label: 'Absent', value: attendanceStats.absent, color: 'red' },
          { label: 'Late', value: attendanceStats.late, color: 'amber' },
          { label: 'Half Day', value: attendanceStats.halfDay, color: 'purple' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Attendance Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Mark Attendance</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="branchId">Branch</Label>
                    <Controller
                      name="branchId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setSelectedBranch(e.target.value);
                          }}
                          error={errors.branchId?.message}
                        >
                          <option value="">Select Branch</option>
                          {branches.map(branch => (
                            <option key={branch.id} value={branch.id}>
                              {branch.branch_name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                </div>

                <div>
                  <Label htmlFor="date">Date</Label>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="date"
                        {...field}
                        error={errors.date?.message}
                      />
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="staffId">Staff Member</Label>
                  <Controller
                    name="staffId"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} error={errors.staffId?.message}>
                        <option value="">Select Staff</option>
                        {staffMembers
                          .filter(staff => !selectedBranch || staff.branch_id === selectedBranch)
                          .map(staff => {
                            const roleName = staff.roles?.[0]?.role_name || 'Staff';
                            return (
                              <option key={staff.id} value={staff.id}>
                                {staff.full_name || staff.email} - {roleName}
                              </option>
                            );
                          })}
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} error={errors.status?.message}>
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                        <option value="late">Late</option>
                        <option value="half-day">Half Day</option>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder="Add any notes..."
                        {...field}
                      />
                    )}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  loading={loading}
                >
                  Mark Attendance
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Attendance List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle>Today's Attendance</CardTitle>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search staff..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="sm:w-40"
                >
                  <option value="all">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="half-day">Half Day</option>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredAttendance.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No attendance records found</p>
                  </div>
                ) : (
                  filteredAttendance.map((record, index) => {
                    const StatusIcon = getStatusIcon(record.status);
                    return (
                      <motion.div
                        key={record.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.5 + index * 0.05 }}
                        className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(record.status)}`}>
                            <StatusIcon size={16} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{record.staffName}</p>
                            <p className="text-sm text-gray-500">{record.role} • {record.branch}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(record.status)}>
                            {record.status}
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">
                            {record.checkIn && <div>In: {record.checkIn}</div>}
                            {record.checkOut && <div>Out: {record.checkOut}</div>}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
              
              {filteredAttendance.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Attendance Rate: <span className="font-bold text-green-600">{attendanceRate}%</span>
                    </span>
                    <span className="text-sm text-gray-500">
                      {filteredAttendance.length} records
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default StaffAttendance;
